// MainPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router";
import { Pickaxe, Twitter, Shield, AlertTriangle, Brain, ChevronRight, ArrowUp, Search, Star, TrendingUp, Settings, Calendar, BookOpen, Activity, ScanSearch, Newspaper, DollarSign, Zap, Info, MessageSquare, Smartphone, X, Download, Mail, Globe, User } from 'lucide-react';
import SearchModal from './SearchModal/SearchModal'; // 새로운 SearchModal 컴포넌트 import
import { AnimatedCircularProgress } from './Progress/AnimatedCircularProgress';
import FullAnalysisModal from './FullAnalysisModal/FullAnalysisModal';
import UNHAnalysisModal from './FullAnalysisModal/FullAnalysisModal2';
import './StockMainPage.css';
import NewsSection from './News/NewsSection';
import MiniTradingView from './TradingView/MiniTradingView';
// import HeatMapTradingView from './TradingView/HeatMapTradingView';
import InvestmentDisclaimer from './Common/InvestmentDisclaimer';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import RankingSection from './Ranking/RankingSection';
import { getDashboardMainContent, getDashboardMarketData, getFavoriteList, getXTweetData, getFearGreedIndex, getStockRanking } from '../services/api';
import { detectDeviceType } from '../utils/device';
import useAuth from '../hooks/useAuth';
import useSearchModal from '../hooks/useSearchModal';

// UUID 수신을 위한 커스텀 이벤트 정의 (컴포넌트 외부)
const UUID_RECEIVED_EVENT = 'uuid-received';

if (typeof window !== 'undefined') {
  // 전역 receiveUUID 함수 정의 (iOS/Android에서 호출)
  window.receiveUUID = function(uuid) {
    console.log('UUID received from native:', uuid);

    // 커스텀 이벤트 발생
    const event = new CustomEvent(UUID_RECEIVED_EVENT, {
      detail: { uuid }
    });
    window.dispatchEvent(event);
  };

  console.log('Global receiveUUID function defined (Event-based)');
}

const StockMainPage = () => {
  const [hasFavorites, setHasFavorites] = useState(false);
  const { isSearchModalOpen, openSearchModal, closeSearchModal } = useSearchModal();
  const [isFullAnalysisModalOpen, setIsFullAnalysisModalOpen] = useState(false);
  const [fearGreedData, setFearGreedData] = useState(null);
  const [mainContentData, setMainContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainContentLoading, setMainContentLoading] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [marketDataLoading, setMarketDataLoading] = useState(true);
  const { accessToken, initAuth } = useAuth();
  const [favoriteList, setFavoriteList] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showAppDownloadSheet, setShowAppDownloadSheet] = useState(false);
  const [xTweetData, setXTweetData] = useState([]);
  const [xTweetLoading, setXTweetLoading] = useState(true);
  const [rankingData, setRankingData] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(true);

  const navigate = useNavigate();
  
  // 주식 로고 이미지 경로 생성
  const getStockLogoPath = (ticker) => {
    return `/CompanyLogo/${ticker}.svg`;
  };

  const handleClick = (stockName) => {
    navigate(`/stockDetail/${stockName}/`);
  };
  // 검색 모달 열기
  // 설정 페이지로 이동
  const goToSettings = (e) => {
    const device = detectDeviceType();
    e.preventDefault();
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeApp) {
      // iOS 네이티브 앱으로 메시지 전송
      window.webkit.messageHandlers.nativeApp.postMessage('goSettingPage');
    } else if (device === 'android') {
      // Android 네이티브 앱 호출 (필요시)
      // window.nativeApp.postMessage('goSettingPage');
      window.goSettingPage.postMessage('');
    } else {
      // 웹 환경에서는 전역 함수 실행
      window.goSettingPage = function() {
        window.webkit.messageHandlers.nativeApp.postMessage('goSettingPage');
      };
      window.goSettingPage();
    }
  };

  // 커뮤니티 페이지로 이동
  const goToCommunity = (e) => {
    e.preventDefault();
    navigate('/stockCommunity');
  };

  // 프로필 페이지로 이동
  const goToProfile = (e) => {
    e.preventDefault();
    navigate('/profile');
  };

  // 앱 다운로드 bottom sheet 닫기
  const closeAppDownloadSheet = () => {
    setShowAppDownloadSheet(false);
    localStorage.setItem('hasSeenAppPromo', 'true');
  };

  // 앱 스토어로 이동
  const goToAppStore = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) {
      // Android - Google Play Store
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      // iOS - App Store
      window.open('https://apps.apple.com/app/6748879356', '_self');
    } else {
      // 기본적으로 Google Play Store로 이동
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    }
    
    closeAppDownloadSheet();
  };



  // 즐겨찾기 목록 가져오기 함수
  const fetchFavoriteList = async (token) => {
    try {
      setFavoriteLoading(true);
      console.log('Fetching favorite list with token:', token);
      const data = await getFavoriteList(token);
      setFavoriteList(data.list || []);
      setHasFavorites(data.list && data.list.length > 0);
      console.log('Favorite list received:', data.list);
    } catch (error) {
      console.error('Error fetching favorite list:', error);
      setFavoriteList([]);
      setHasFavorites(false);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // 공포 탐욕 지수 API 호출
  const fetchFearGreedIndexData = async () => {
    try {
      setLoading(true);
      const data = await getFearGreedIndex();
      setFearGreedData(data.fearAndGreed);
    } catch (error) {
      console.error('Error fetching fear and greed index:', error);
    } finally {
      setLoading(false);
    }
  };

  // 대시보드 메인 컨텐츠 API 호출
  const fetchMainContent = async () => {
    try {
      setMainContentLoading(true);
      const data = await getDashboardMainContent();
      setMainContentData(data);
    } catch (error) {
      console.error('Error fetching main content:', error);
    } finally {
      setMainContentLoading(false);
    }
  };

  // 시장 데이터 API 호출
  const fetchMarketData = async () => {
    try {
      setMarketDataLoading(true);
      const data = await getDashboardMarketData();
      setMarketData(data);
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      setMarketDataLoading(false);
    }
  };

  // X 트윗 데이터 API 호출
  const fetchXTweetData = async () => {
    try {
      setXTweetLoading(true);
      const data = await getXTweetData();
      setXTweetData(data.list || []);
    } catch (error) {
      console.error('Error fetching X tweet data:', error);
    } finally {
      setXTweetLoading(false);
    }
  };

  // 주식 랭킹 데이터 API 호출
  const fetchRankingData = async () => {
    try {
      setRankingLoading(true);
      const data = await getStockRanking();
      setRankingData(data);
    } catch (error) {
      console.error('Error fetching stock ranking:', error);
    } finally {
      setRankingLoading(false);
    }
  };

  // 웹 사용자 첫 방문 체크 및 앱 다운로드 유도
  const checkWebFirstVisit = () => {
    // 웹 브라우저이고 앱 프로모를 본 적이 없으면 bottom sheet 표시
      setTimeout(() => {
    const hasSeenAppPromo = localStorage.getItem('hasSeenAppPromo');
    const userUUID = localStorage.getItem('lastKnownUUID');
    if (userUUID == null && !hasSeenAppPromo) {
      console.log('Web user UUID exists, showing app download sheet');
        setShowAppDownloadSheet(true);
      }
    }, 4000); // 4초 후 표시
  };

  // 각 섹션의 렌더링 함수들
  // const renderHeatmapSection = () => (
  //   <section className="section">
  //     <div className="favorites-grid">
  //       <div className="relative">
  //         <HeatMapTradingView></HeatMapTradingView>

  //         {/* 데이터 기준 안내 툴팁 */}
  //         <div className="absolute top-4 left-0 z-9999">
  //           <button
  //             className="relative"
  //             onMouseEnter={() => setShowTooltip('heatmap')}
  //             onMouseLeave={() => setShowTooltip(null)}
  //             onClick={() => setShowTooltip(showTooltip === 'heatmap' ? null : 'heatmap')}
  //           >
  //             <div className="w-5 h-5 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 rounded-full flex items-center justify-center transition-all duration-200">
  //               <Info size={12} className="text-white" />
  //             </div>
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   </section>
  // );

  const renderAiAnalysisSection = () => (
    <div className="feature-section">
      <div className="flex items-center justify-between gap-1 mb-4">
        {/* 좌측: 아이콘 + 제목 + 설명글 */}
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <div className="feature-icon" style={{ width: '32px', height: '32px' }}>
              <Brain size={24} />
            </div>
            <h2 className="text-xm font-bold text-black">AI 전체 분석</h2>
          </div>
          <p className="text-xs text-gray-600 pl-1">
            국제 정세와 시장 심리를 한눈에 파악하세요
          </p>
        </div>

        {/* 우측: 스코어 */}
        <AnimatedCircularProgress
            key={`main-score-${mainContentData?.score || 0}`}
            score={mainContentData?.score || 0}
            size={76}
            delay={300}
          />
      </div>

      {mainContentLoading ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 mb-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={16} className="text-blue-600 flex-shrink-0" />
            <h3 className="text-sm font-bold text-gray-900">AI 핵심 요약</h3>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            {mainContentData?.aiSummary || '시장 분석 데이터를 불러오는 중입니다...'}
          </p>
        </div>
      )}

      {marketDataLoading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-3 w-32"></div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      ) : marketData ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Activity size={16} className="text-indigo-600" />
            국제 정세 스냅샷
          </h3>

      {/* 주요 지표 요약 */}
      {marketDataLoading ? (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="bg-gray-50 p-3 rounded-lg animate-pulse">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="h-5 bg-gray-200 rounded w-12 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-8"></div>
            </div>
          ))}
        </div>
      ) : marketData ? (
        <div className="grid grid-cols-2 gap-2 mb-4">

          {/* VIX 공포지수 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className={`${marketData.vix > 20 ? 'text-red-500' : marketData.vix > 15 ? 'text-orange-500' : 'text-green-500'}`} />
              <span className="text-xs font-medium text-gray-700">VIX 지수</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('vix')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'vix' && (
                  <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">😨 VIX 공포지수 (Volatility Index)</div>
                    <div className="mb-2 text-gray-200">투자자들의 공포 정도를 측정하는 지표로, 시장 변동성과 불안감을 나타냅니다.</div>
                    <div className="text-yellow-200">• 10-20: 안정적 (투자 좋은 환경)</div>
                    <div className="text-yellow-200">• 20-30: 불안함 (주의 필요)</div>
                    <div className="text-yellow-200">• 30이상: 극도 공포 (위기 상황)</div>
                  </div>
                )}
              </button>
            </div>
            <span className={`text-sm font-bold ${marketData.vix > 20 ? 'text-red-600' : marketData.vix > 15 ? 'text-orange-600' : 'text-green-600'}`}>
              {marketData.vix?.toFixed(1)}
            </span>
            <span className={`text-xs ml-1 ${marketData.changes?.vix > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {marketData.changes?.vix > 0 ? '+' : ''}{marketData.changes?.vix?.toFixed(1)}%
            </span>
          </div>

          {/* S&P 500 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-blue-500" />
              <span className="text-xs font-medium text-gray-700">S&P 500</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('spx')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'spx' && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">📈 S&P 500 지수</div>
                    <div className="mb-2 text-gray-200">미국 대형주 500개 기업의 주가를 종합한 지수로, 미국 전체 주식시장의 80%를 대표합니다.</div>
                    <div className="text-yellow-200">• 상승: 미국 경제 호조, 글로벌 위험자산 선호</div>
                    <div className="text-yellow-200">• 하락: 미국 경제 우려, 글로벌 주식 부진</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-blue-600">
              {marketData.spx?.toLocaleString('ko-KR', {maximumFractionDigits: 1})}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.spx > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.spx > 0 ? '+' : ''}{marketData.changes?.spx?.toFixed(1)}%
            </span>
          </div>

          {/* 러셀 2000 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-purple-500" />
              <span className="text-xs font-medium text-gray-700">러셀 2000</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('russell')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'russell' && (
                  <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">🏢 러셀 2000 지수</div>
                    <div className="mb-2 text-gray-200">미국 소형주 2000개 기업의 주가 지수로,<p>'동네 상권' 같은 서민경제를 대표합니다.</p></div>
                    <div className="text-yellow-200">• 대형주 대비 상승: 경기 회복 기대감</div>
                    <div className="text-yellow-200">• 대형주 대비 하락: 경기 우려, 안전 선호</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-purple-600">
              {marketData.russell2k?.toLocaleString('ko-KR', {maximumFractionDigits: 1})}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.russell2k > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.russell2k > 0 ? '+' : ''}{marketData.changes?.russell2k?.toFixed(1)}%
            </span>
          </div>

          {/* 달러 지수 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-green-500" />
              <span className="text-xs font-medium text-gray-700">달러 지수</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('dxy')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'dxy' && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">💵 달러 지수 (DXY)</div>
                    <div className="mb-2 text-gray-200">달러의 강세를 측정하는 지표로, 주요 6개(EUR, JPY, GBP, CHF, CAD, SEK) 통화 대비 달러 가치를 나타냅니다.</div>
                    <div className="text-yellow-200">• 상승: 달러 강세, 금 약세, 신흥국 부진</div>
                    <div className="text-yellow-200">• 하락: 달러 약세, 금 강세, 신흥국 호조</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-green-600">
              {marketData.dxy?.toFixed(1)}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.dxy > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.dxy > 0 ? '+' : ''}{marketData.changes?.dxy?.toFixed(1)}%
            </span>
          </div>

          {/* 금 가격 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Pickaxe size={16} className="text-yellow-600" />
              <span className="text-xs font-medium text-gray-700">금 가격</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('gold')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'gold' && (
                  <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">🥇 금 가격 (Gold Price)</div>
                    <div className="mb-2 text-gray-200">대표적인 안전자산으로, 인플레이션과 경제 불안정 시기에 선호되는 투자처입니다.</div>
                    <div className="text-yellow-200">• 상승: 인플레이션 우려, 달러 약세, 지정학 리스크</div>
                    <div className="text-yellow-200">• 하락: 경제 안정, 달러 강세, 금리 상승</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-yellow-700">
              ${marketData.gold_price?.toLocaleString('ko-KR', {maximumFractionDigits: 1})}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.gold > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.gold > 0 ? '+' : ''}{marketData.changes?.gold?.toFixed(1)}%
            </span>
          </div>

          {/* WTI 원유 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 bg-black rounded-full"></div>
              <span className="text-xs font-medium text-gray-700">WTI 원유</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('wti')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'wti' && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">🛢️ WTI 원유 (West Texas Intermediate)</div>
                    <div className="mb-2 text-gray-200">인플레이션의 '대장' 역할을 하는 핵심 에너지 원자재로, 전 세계 물가에 직접 영향을 미칩니다.</div>
                    <div className="text-yellow-200">• 상승: 운송비→전기요금→전반적 물가 상승</div>
                    <div className="text-yellow-200">• 하락: 인플레이션 압력 감소, 경제에 긍정적</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-gray-800">
              ${marketData.wti_price?.toFixed(2)}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.wti_oil > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.wti_oil > 0 ? '+' : ''}{marketData.changes?.wti_oil?.toFixed(1)}%
            </span>
          </div>

          {/* 구리 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Pickaxe size={16} className="text-orange-600" />
              <span className="text-xs font-medium text-gray-700">구리</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('copper')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'copper' && (
                  <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">⚡ 구리 (Doctor Copper)</div>
                    <div className="mb-2 text-gray-200">경제학 박사 닉네임을 가진 원자재로, 건설·전자·자동차 등 모든 산업에 필수입니다.</div>
                    <div className="text-yellow-200">• 상승: 경기 회복, 건설및제조업 호황</div>
                    <div className="text-yellow-200">• 하락: 경기 둔화, 산업 수요 감소</div>
                    <div className="text-yellow-200">• 금리와 역상관: 경기 좋음 → 금리 상승 우려</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-orange-700">
              ${marketData.copper_price?.toFixed(3)}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.copper > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.copper > 0 ? '+' : ''}{marketData.changes?.copper?.toFixed(1)}%
            </span>
          </div>

          {/* USD/JPY */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 bg-blue-600 rounded-sm"></div>
              <span className="text-xs font-medium text-gray-700">USD/JPY</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('usdjpy')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'usdjpy' && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">🇯🇵 엔케리 트레이드 (Yen Carry Trade)</div>
                    <div className="mb-2 text-gray-200">일본에서 저금리로 엔화를 비리고, 미국 등 고금리 국가에 투자하는 전략입니다.</div>
                    <div className="text-yellow-200">• 엔화 약세(USD/JPY ↑): 케리 트레이드 좋음</div>
                    <div className="text-yellow-200">• 엔화 강세(USD/JPY ↓): 케리 청산 위험</div>
                    <div className="text-yellow-200">• 145엔 이상: 일본 정부 개입 우려</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-blue-700">
              {marketData.usdjpy?.toFixed(1)}
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.usdjpy > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {marketData.changes?.usdjpy > 0 ? '+' : ''}{marketData.changes?.usdjpy?.toFixed(2)}%
            </span>
          </div>

          {/* 미국 2년 국채 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-red-500" />
              <span className="text-xs font-medium text-gray-700">2년 국채</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('us2y')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'us2y' && (
                  <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">📈 미국 2년 국채 수익률</div>
                    <div className="mb-2 text-gray-200">단기 금리 동향과 연준 통화정책 방향성을 보여주는 핵심 지표입니다.</div>
                    <div className="text-yellow-200">• 상승: 인플레이션 우려, 연준 금리 인상 기대</div>
                    <div className="text-yellow-200">• 하락: 경기 둔화 우려, 연준 금리 인하 기대</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-red-600">
              {marketData.us_2y_yield?.toFixed(1)}%
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.us_2y > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {marketData.changes?.us_2y > 0 ? '+' : ''}{marketData.changes?.us_2y?.toFixed(1)}%
            </span>
          </div>

          {/* 미국 10년 국채 수익률 추가 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-indigo-500" />
              <span className="text-xs font-medium text-gray-700">10년 국채</span>
              <button
                className="ml-0 relative"
                onMouseEnter={() => setShowTooltip('us10y')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'us10y' && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <div className="mb-2 font-semibold">📉 미국 10년 국채 수익률</div>
                    <div className="mb-2 text-gray-200">장기 금리 동향과 경제 성장 기대를 나타내는 글로벌 기준 금리입니다.</div>
                    <div className="text-yellow-200">• 상승: 인플레이션·경제성장 기대, 주식에 부담</div>
                    <div className="text-yellow-200">• 하락: 경기 둔화 우려, 주식에 긍정적</div>
                    <div className="text-yellow-200">• 2년-10년 역전: 경기 침체 신호</div>
                  </div>
                )}
              </button>
            </div>
            <span className="text-sm font-bold text-indigo-600">
              {marketData.us_10y_yield?.toFixed(1)}%
            </span>
            <span className={`text-xs text-gray-500 ml-1 ${marketData.changes?.us_10y > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {marketData.changes?.us_10y > 0 ? '+' : ''}{marketData.changes?.us_10y?.toFixed(1)}%
            </span>
          </div>

        </div>
      ) : null}
        <div className="text-xs text-gray-400 text-right">
          {loading ? 'Loading...' : `${mainContentData?.createDateTime ? new Date(mainContentData?.createDateTime).toLocaleString('ko-KR') : 'N/A'}`}
        </div>
        </div>
      ) : null}
      {!mainContentLoading && (
        <button className="view-full-button mb-4"
            onClick={() => setIsFullAnalysisModalOpen(true)}>
            <span>전체 분석 보기</span>
            <ChevronRight size={16} />
        </button>
      )}

      </div>
  );

  const renderRankingSection = () => (
    !rankingLoading && rankingData && (
      <RankingSection
        rankingData={rankingData}
        onStockClick={handleClick}
      />
    )
  );

  const renderFavoritesSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon" style={{ width: '32px', height: '32px' }}>
          <ScanSearch size={24} />
        </div>
        <h2 className="text-xl font-bold text-black">관심 기업</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        즐겨찾기한 주식들의 실시간 가격과 변동률을 확인하고 빠른 투자 결정을 내리세요.
      </p>

      {favoriteLoading ? (
        <div className="card animate-pulse">
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
        </div>
      ) : hasFavorites && favoriteList.length > 0 ? (
        <div className="card">
          {favoriteList.map((favorite, index) => (
            <div
              key={favorite.userFavoriteTickerNo || index}
              className={`flex justify-between items-center py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                index < favoriteList.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              onClick={() => handleClick(favorite.ticker)}
            >
              <div className="flex items-center">

                <div>
                  <div className="flex items-start">
                    <div className="stock-icon"
                        style={{ width: '60px', height: '60px'}}>
                      <img
                        src={getStockLogoPath(favorite.ticker)}
                        alt={favorite.nameKr || favorite.ticker}
                      />
                      <TrendingUp size={20} className="fallback-icon" />
                    </div>
                    <div className="stock-info">
                      <div className="stock-symbol">{favorite.ticker}</div>
                        <div className="stock-name">
                          {favorite.nameKr && <div>{favorite.nameKr}</div>}
                          {favorite.nameEn && <div>{favorite.nameEn}</div>}
                        </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <MiniTradingView symbol={`${favorite.ticker}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="add-favorites-card">
          <Star className="add-star text-blue-400" />
          <h3 className="add-favorites-title">즐겨찾기 추가</h3>
          <p className="add-favorites-desc">
            관심 있는 주식을 즐겨찾기에 추가하여 빠르게 확인하세요
          </p>
          <button
            className="view-full-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openSearchModal(e);
            }}
          >
            <Search size={16} />
            <span>주식 검색</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderFearGreedSection = () => (
    <div className="feature-section">

      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon" style={{ width: '32px', height: '32px' }}>
          <Activity size={24} />
        </div>
        <h2 className="text-xl font-bold text-black">공포 탐욕 지수</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        시장 투자자들의 감정 상태를 측정하여 현재 시장이 과매수인지 과매도인지 파악하세요.
      </p>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {loading ? 'Loading...' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return '현재 공포탐욕지수: 극심한 공포';
                if (score <= 45) return '현재 공포탐욕지수: 공포';
                if (score <= 55) return '현재 공포탐욕지수: 중립';
                if (score <= 75) return '현재 공포탐욕지수: 탐욕';
                return '현재 공포탐욕: 과한 탐욕';
              })()}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {loading ? 'Fetching data...' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return '투자자들이 매우 공포에 빠져 있습니다';
                if (score <= 45) return '투자자들이 공포를 느끼고 있습니다';
                if (score <= 55) return '투자자들이 중립적인 상태입니다';
                if (score <= 75) return '투자자들이 탐욕을 느끼고 있습니다';
                return '투자자들이 매우 탐욕적입니다';
              })()}
            </p>
          </div>
          <div className="flex items-center">
            <span className={`text-2xl font-bold ml-2 ${loading ? 'text-gray-900' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return 'text-red-500';
                if (score <= 45) return 'text-orange-500';
                if (score <= 55) return 'text-yellow-500';
                if (score <= 75) return 'text-green-500';
                return 'text-green-700';
              })()}`}>
              {loading ? 'Loading...' : fearGreedData?.score.toFixed(0) || 'N/A'}
            </span>
          </div>
        </div>

        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2 relative">
          <div className="flex h-full">
            <div style={{ width: '25%' }} className="bg-red-500"></div>
            <div style={{ width: '20%' }} className="bg-orange-500"></div>
            <div style={{ width: '10%' }} className="bg-yellow-400"></div>
            <div style={{ width: '20%' }} className="bg-green-500"></div>
            <div style={{ width: '25%' }} className="bg-green-700"></div>
          </div>
          {!loading && fearGreedData && (
            <div
              className="absolute top-0 w-1 h-full bg-black z-10"
              style={{
                left: `${fearGreedData.score}%`,
                transform: 'translateX(-50%)'
              }}
            />
          )}
        </div>

        <div className="flex justify-between mb-4 text-xs text-gray-500">
          <span>극심한 공포</span>
          <span>공포</span>
          <span>중립</span>
          <span>탐욕</span>
          <span>과한 탐욕</span>
        </div>

        <div className="text-xs text-gray-400 text-right">
          {loading ? 'Loading...' : `${fearGreedData?.KST ? new Date(fearGreedData.KST).toLocaleString('ko-KR') : 'N/A'}`}
        </div>
      </div>
    </div>
  );

  const renderNewsSection = () => (
    <NewsSection></NewsSection>
  );

  const renderTwitterSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon" style={{ width: '32px', height: '32px' }}>
          <Twitter size={24} />
        </div>
        <h2 className="text-xl font-bold text-black">주요 인물 SNS</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        주요 인물들의 X(트위터)를 통해 최신 시장 동향과 리스크를 실시간으로 확인하세요.
      </p>

      {xTweetLoading ? (
        <div className="bg-white rounded-lg p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-4/5"></div>
            <div className="h-4 bg-gray-200 rounded w-3/5"></div>
          </div>
        </div>
      ) : xTweetData.length > 0 ? (
        <div className="space-y-4">
          {xTweetData.map((tweet, index) => (
            <TwitterTweetEmbed
              key={tweet.xinfoNo}
              tweetId={tweet.xid}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="text-gray-500 mb-2">트윗을 불러올 수 없습니다</div>
          <button
            onClick={fetchXTweetData}
            className="text-blue-600 text-sm hover:underline"
          >
            다시 시도
          </button>
        </div>
      )}
    </div>
  );

  // accessToken 발급 완료 시 즐겨찾기 목록 자동 로드
  useEffect(() => {
    if (accessToken) {
      fetchFavoriteList(accessToken);
    }
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // 컴포넌트 마운트 시 API 호출
  useEffect(() => {
    console.log('StockMainPage mounted, receiveUUID available:', typeof window.receiveUUID);

    fetchFearGreedIndexData();
    fetchMainContent();
    fetchMarketData();
    fetchXTweetData();
    fetchRankingData();
    initAuth();
    checkWebFirstVisit();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="main-container">
      {/* 상단 헤더 */}
      <header className="header">
        <div className="header-content">
          <div className="header-row">
            <div className="logo-container">
              <img src="/logo512.png" alt="Logo" className="logo" />
            </div>
            <div className="header-actions">
              <div 
                className="icon-button" 
                role="button"
                tabIndex={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openSearchModal(e);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Search size={20} />
              </div>
              <button type="button" className="icon-button" onClick={goToCommunity}>
                <MessageSquare size={20} />
              </button>
              {/* <button type="button" className="icon-button" onClick={goToProfile}>
                <User size={20} />
              </button> */}
              <button type="button" className="icon-button" onClick={goToSettings}>
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="main-content">

      {/* 투자 면책 조항 */}
      <InvestmentDisclaimer />

      {/* AI 전체 분석 */}
      {renderAiAnalysisSection()}

      {/* 주식 랭킹 */}
      {renderRankingSection()}

      {/* 관심 기업 */}
      {renderFavoritesSection()}

      {/* 공포 탐욕 지수 */}
      {renderFearGreedSection()}

      {/* 뉴스 */}
      {renderNewsSection()}

      {/* X(트위터) */}
      {renderTwitterSection()}

{/* 다가오는 이벤트 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <Calendar size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">중요한 이벤트</h2>
  <p className="text-sm text-gray-600 mb-4">
    다가오는 중요한 경제 이벤트와 실적 발표 일정을 미리 확인하고 대비하세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Quadruple Witching Day
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The simultaneous expiration of stock index futures, stock index options, 
          stock options, and single stock futures on the third Friday of March, June, 
          September, and December...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 21, 2025</span>
      </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Fed Interest Rate Decision
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Federal Open Market Committee (FOMC) announces its decision 
          on interest rates. Market expects rates to remain unchanged at 5.25-5.50%...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 26, 2025</span>
      </div>
  </div>
</div> */}

{/* 주식 용어 팁 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <BookOpen size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">투자 지식</h2>
  <p className="text-sm text-gray-600 mb-4">
    주식 투자에 필요한 핵심 용어와 개념을 쉽게 이해하고 투자 실력을 향상시키세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          What is Quadruple Witching?
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          "Quadruple Witching" refers to the simultaneous expiration of stock index futures, 
          stock index options, stock options, and single stock futures. This happens four times 
          a year on the third Friday of March, June, September, and December. These days typically 
          see increased trading volume and volatility...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Understanding Price-to-Earnings (P/E) Ratio
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Price-to-Earnings (P/E) ratio is a valuation metric that compares a company's 
          current share price to its earnings per share (EPS). A high P/E ratio could mean 
          a stock is overvalued or that investors expect high growth rates in the future...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>
</div> */}
      </main>

      {/* 모달 */}
      <FullAnalysisModal 
        isOpen={isFullAnalysisModalOpen} 
        onClose={() => setIsFullAnalysisModalOpen(false)} 
        analysisText={mainContentData?.mainContent || "시장 분석 데이터를 불러오는 중입니다..."}
        stockSymbol="시장 전체"
        reportType="market"
      />

      {/* 검색 모달 */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onStockClick={handleClick}
      />
      
      {/* 앱 다운로드 유도 Bottom Sheet */}
      {showAppDownloadSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 animate-fadeIn2">
          <div className="bg-white rounded-t-2xl w-full max-w-md mb-0 transform animate-slideUp2">
            <div className="p-6">
              {/* 닫기 버튼 */}
              <button 
                onClick={closeAppDownloadSheet}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>

              {/* 헤더 */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 p-3">
                  <img src="/logo.png" alt="App Logo" className="w-full h-full object-contain" />
                </div>
                <p className="text-gray-600 font-bold text-l leading-relaxed">
                  더 나은 경험을 위해 모바일 앱을 다운로드하세요!
                </p>
              </div>

              {/* 기능 리스트 */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">주식 즐겨찾기</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">더 빠른 로딩 속도</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">커뮤니티 이용 가능</span>
                </div>
              </div>

              {/* 액션 버튼들 */}
              <div className="space-y-3">
                <button 
                  onClick={goToAppStore}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
                >
                  <Download size={20} />
                  앱 다운로드
                </button>
                <button 
                  onClick={closeAppDownloadSheet}
                  className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition-colors"
                >
                  나중에 다운로드하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Footer */}
      <footer className="mt-12 py-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Contact Us</h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
            {/* Email */}
            <a
              href="mailto:mamo.studio93@gmail.com"
              className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              <Mail size={20} />
              <span className="text-sm">mamo.studio93@gmail.com</span>
            </a>

            {/* Website */}
            <a
              href="https://mmstudio.co.kr/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              <Globe size={20} />
              <span className="text-sm">https://mmstudio.co.kr/</span>
            </a>

            {/* X (Twitter) */}
            <a
              href="https://x.com/investing_sf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              <Twitter size={20} />
              <span className="text-sm">@investing_sf</span>
            </a>
          </div>
          <p className="text-xs text-gray-500 text-center mt-6">
            © 2026 투자특공대. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StockMainPage;
